import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import { SkeletonCard } from '../components/SkeletonCard';
import PremiumModal from '../components/PremiumModal';
import toast from 'react-hot-toast';

const STATUS_COLORS = { working:'#16A34A', busy:'#D97706', broken:'#DC2626', unknown:'#64748B' };

export default function ChargerDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saved, setSaved]           = useState(false);
  const [reporting, setReporting]   = useState(false);
  const [status, setStatus]         = useState('');
  const [comment, setComment]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPremium, setShowPremium] = useState(false);
  const [premiumReason, setPremiumReason] = useState('save_limit');
  const loggedIn = !!localStorage.getItem('evapp_token');

  useEffect(() => {
    api.get(`/chargers/${slug}`)
      .then(r => { setData(r.data); setSaved(r.data.saved); })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [slug]);

  const toggleSave = async () => {
    if (!loggedIn) { navigate(`/login?next=/charger/${slug}`); return; }
    try {
      if (saved) {
        await api.delete(`/saved/${data.charger.id}`);
        setSaved(false); toast.success('Removed from saved');
      } else {
        await api.post(`/saved/${data.charger.id}`, { notify: true });
        setSaved(true); toast.success('Saved! You will get alerts.');
      }
    } catch (err) {
      if (err.response?.data?.upgrade_required) {
        setPremiumReason('save_limit'); setShowPremium(true);
      } else { toast.error('Failed'); }
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (!loggedIn) { navigate(`/login?next=/charger/${slug}`); return; }
    if (!status) { toast.error('Select a status'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/reports', { charger_id: data.charger.id, status, comment });
      toast.success(res.data.points_earned ? `+${res.data.points_earned} points!` : 'Report submitted!');
      setReporting(false); setStatus(''); setComment('');
      const updated = await api.get(`/chargers/${slug}`);
      setData(updated.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const openNav = () => {
    const { lat, lng } = data.charger;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank');
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', padding:'80px 16px' }}>
      <div style={{ maxWidth:640, margin:'0 auto', display:'flex', flexDirection:'column', gap:12 }}>
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
    </div>
  );

  if (!data) return null;
  const { charger, reports } = data;
  const score = Number(charger.reliability_score);
  const scoreColor = score >= 7 ? '#16A34A' : score >= 4 ? '#D97706' : '#DC2626';
  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, background:'var(--slate-2)', border:'1px solid var(--border-2)', color:'var(--text-1)', fontSize:13, fontFamily:'Inter,sans-serif', boxSizing:'border-box' };

  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', paddingTop:56, fontFamily:'Inter,sans-serif' }}>
      {showPremium && <PremiumModal reason={premiumReason} onClose={() => setShowPremium(false)} />}

      <div style={{ background:'var(--slate)', borderBottom:'1px solid var(--border)', padding:'24px 20px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ maxWidth:640, margin:'0 auto' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <h1 style={{ fontSize:22, fontWeight:800, color:'var(--text-1)', marginBottom:6, letterSpacing:'-0.4px', lineHeight:1.3 }}>{charger.name}</h1>
              <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:8 }}>📍 {[charger.address, charger.city, charger.country].filter(Boolean).join(', ')}</p>
              {charger.connector_types?.length > 0 && (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {charger.connector_types.map(ct => (
                    <span key={ct} style={{ fontSize:11, padding:'2px 8px', borderRadius:8, background:'var(--teal-dim)', color:'var(--teal)', border:'1px solid var(--teal-mid)', fontWeight:600 }}>{ct}</span>
                  ))}
                </div>
              )}
            </div>
            <StatusBadge status={charger.status} />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={openNav} style={{ flex:1, padding:'11px 0', borderRadius:10, border:'none', background:'var(--teal)', color:'white', fontWeight:700, fontSize:14, cursor:'pointer' }}>🧭 Navigate</button>
            <button onClick={toggleSave} style={{ flex:1, padding:'11px 0', borderRadius:10, border:`1px solid ${saved?'#DC262633':'var(--border-2)'}`, background:saved?'#DC262611':'none', color:saved?'#DC2626':'var(--text-2)', fontWeight:600, fontSize:14, cursor:'pointer' }}>
              {saved ? '♥ Saved' : '♡ Save & Alert'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:640, margin:'0 auto', padding:'16px 16px 60px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
          {[
            { label:'Reliability', value:`${score}/10`, color:scoreColor },
            { label:'Reports', value:charger.report_count||0, color:'var(--text-1)' },
            { label:'Power', value:charger.power_kw?`${charger.power_kw}kW`:'—', color:'var(--text-1)' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 10px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize:22, fontWeight:800, color, fontFamily:'JetBrains Mono,monospace', marginBottom:4 }}>{value}</div>
              <div style={{ fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
            <span style={{ fontSize:13, fontWeight:600, color:'var(--text-1)' }}>Reliability Score</span>
            <span style={{ fontSize:13, color:scoreColor, fontWeight:700 }}>{score}/10</span>
          </div>
          <div style={{ height:8, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${score*10}%`, background:scoreColor, borderRadius:4 }} />
          </div>
          <div style={{ fontSize:11, color:'var(--text-3)', marginTop:6 }}>
            Based on {charger.report_count||0} community reports
            {charger.last_report_at && ` · Last: ${new Date(charger.last_report_at).toLocaleDateString()}`}
          </div>
        </div>

        <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
          {!reporting ? (
            <button onClick={() => setReporting(true)} style={{ width:'100%', padding:'11px 0', borderRadius:10, border:'1px solid var(--border-2)', background:'none', color:'var(--text-1)', fontWeight:600, fontSize:14, cursor:'pointer' }}>
              📋 Report Current Status
            </button>
          ) : (
            <form onSubmit={submitReport}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:'var(--text-1)' }}>What is the current status?</div>
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                {['working','busy','broken'].map(s => (
                  <button key={s} type="button" onClick={() => setStatus(s)} style={{ flex:1, padding:'9px 0', borderRadius:9, border:`1px solid ${STATUS_COLORS[s]}44`, background:status===s?STATUS_COLORS[s]+'22':'transparent', color:STATUS_COLORS[s], fontWeight:700, fontSize:12, cursor:'pointer', textTransform:'capitalize' }}>
                    {s==='working'?'✅':s==='busy'?'🟡':'🔴'} {s}
                  </button>
                ))}
              </div>
              <textarea placeholder="Optional comment…" value={comment} onChange={e => setComment(e.target.value)} maxLength={500} style={{ ...inp, height:72, resize:'none', marginBottom:10 }} />
              <div style={{ display:'flex', gap:8 }}>
                <button type="submit" disabled={!status||submitting} style={{ flex:1, padding:'10px 0', borderRadius:9, border:'none', background:'var(--teal)', color:'white', fontWeight:700, fontSize:13, cursor:'pointer', opacity:status?1:0.4 }}>
                  {submitting?'Submitting…':'Submit +10pts'}
                </button>
                <button type="button" onClick={() => setReporting(false)} style={{ padding:'10px 14px', borderRadius:9, border:'1px solid var(--border-2)', background:'none', color:'var(--text-3)', fontSize:13, cursor:'pointer' }}>Cancel</button>
              </div>
            </form>
          )}
        </div>

        {reports?.length > 0 && (
          <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:16, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:12, color:'var(--text-1)' }}>Recent Reports ({reports.length})</div>
            {reports.slice(0,5).map((r,i) => (
              <div key={r.id} style={{ display:'flex', gap:10, paddingTop:i>0?10:0, borderTop:i>0?'1px solid var(--border)':'none', paddingBottom:10 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:STATUS_COLORS[r.status]||'#64748B', marginTop:4, flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:STATUS_COLORS[r.status], textTransform:'capitalize' }}>{r.status}</span>
                    <span style={{ fontSize:11, color:'var(--text-3)' }}>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.comment && <div style={{ fontSize:12, color:'var(--text-2)', lineHeight:1.5 }}>{r.comment}</div>}
                  <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>
                    {r.reporter_name||'Anonymous'}
                    {r.verified && <span style={{ color:'var(--teal)', marginLeft:6 }}>✓ Verified</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Link to="/" style={{ color:'var(--teal)', fontSize:13, textDecoration:'none', fontWeight:500 }}>← Back to map</Link>
      </div>
    </div>
  );
}
